import { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Link as LinkIcon,
  Lock,
  Users,
  Heart,
  CalendarDays,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useCreateRecord, useUpdateRecord } from '../hooks/use-patient-records'
import { useUploadAttachment } from '../hooks/use-upload-attachment'
import { RecordAttachmentsUpload, type PendingFile } from './RecordAttachments'
import { AudioRecorder } from './AudioRecorder'
import { TagSelector } from './TagSelector'
import { useAuth } from '@/features/auth'
import { useToast } from '@/hooks/use-toast'
import type { PatientRecordWithRelations } from '@repo/shared'

// ============================================================
// Form schema
// ============================================================

function getCurrentDateTimeLocal(): string {
  const now = new Date()
  const offset = now.getTimezoneOffset()
  const local = new Date(now.getTime() - offset * 60000)
  return local.toISOString().slice(0, 16)
}

const recordFormSchema = z.object({
  content: z.string().min(1, 'Conteudo e obrigatorio'),
  visibility: z.enum(['private', 'shared', 'family']).default('shared'),
  tag_ids: z.array(z.string()).optional(),
  record_date: z.string().optional().refine(
    (val) => {
      if (!val) return true
      return new Date(val) <= new Date()
    },
    { message: 'A data do registro nao pode ser futura' },
  ),
})

type RecordFormData = z.infer<typeof recordFormSchema>

// ============================================================
// Props
// ============================================================

interface RecordFormProps {
  patientId: string
  record?: PatientRecordWithRelations
  initialContent?: string
  onSuccess?: () => void
  onCancel?: () => void
  onDirtyChange?: (dirty: boolean) => void
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message) return error.message
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message
  }
  return fallback
}

// ============================================================
// Component
// ============================================================

export function RecordForm({
  patientId,
  record,
  initialContent,
  onSuccess,
  onCancel,
  onDirtyChange,
}: RecordFormProps) {
  const isEditMode = !!record
  const createRecord = useCreateRecord()
  const updateRecord = useUpdateRecord()
  const uploadAttachment = useUploadAttachment()
  const { currentOrganization } = useAuth()
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])

  const form = useForm<RecordFormData>({
    resolver: zodResolver(recordFormSchema),
    defaultValues: {
      content: record?.content ?? initialContent ?? '',
      visibility: record?.visibility ?? 'shared',
      tag_ids: record?.tags?.map((t) => t.id) ?? [],
      record_date: record?.record_date
        ? new Date(record.record_date).toISOString().slice(0, 16)
        : getCurrentDateTimeLocal(),
    },
  })

  const contentValue = form.watch('content')
  useEffect(() => {
    const hasContent =
      !!contentValue && contentValue !== '' && contentValue !== '<p></p>'
    onDirtyChange?.(hasContent || pendingFiles.length > 0)
  }, [contentValue, pendingFiles, onDirtyChange])

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-teal-600 underline hover:text-teal-700',
        },
      }),
      Placeholder.configure({
        placeholder: 'Escreva o registro de evolucao do paciente...',
      }),
    ],
    content: record?.content ?? '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      form.setValue('content', html === '<p></p>' ? '' : html, {
        shouldValidate: true,
      })
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none focus:outline-none min-h-[120px] px-3 py-2 [&>*:first-child]:mt-0',
      },
    },
  })

  const handleAddFiles = (files: File[]) => {
    const newFiles = files.map((file) => ({ file }))
    setPendingFiles((prev) => [...prev, ...newFiles].slice(0, 10))
  }

  const handleTranscribed = (text: string) => {
    if (!editor) return
    // Insert the transcribed text at the end of the editor content
    editor.chain().focus().insertContent(`<p>${text}</p>`).run()
  }

  const handleRemoveFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (data: RecordFormData) => {
    setIsSubmitting(true)
    try {
      if (isEditMode && record) {
        await updateRecord.mutateAsync({
          id: record.id,
          patient_id: patientId,
          content: data.content,
          visibility: data.visibility,
          tag_ids: data.tag_ids,
          record_date: data.record_date ? new Date(data.record_date).toISOString() : undefined,
        })
        toast({
          title: 'Registro atualizado',
          description: 'O registro foi atualizado com sucesso.',
        })
      } else {
        const newRecord = await createRecord.mutateAsync({
          patient_id: patientId,
          content: data.content,
          visibility: data.visibility,
          tag_ids: data.tag_ids,
          record_date: data.record_date ? new Date(data.record_date).toISOString() : undefined,
        })

        // Upload pending attachments
        if (pendingFiles.length > 0 && currentOrganization) {
          const uploadResults = await Promise.allSettled(
            pendingFiles.map((pf) =>
              uploadAttachment.mutateAsync({
                recordId: newRecord.id,
                file: pf.file,
                organizationId: currentOrganization.id,
                patientId,
              }),
            ),
          )

          const failures = uploadResults.filter((r) => r.status === 'rejected')
          if (failures.length > 0) {
            toast({
              title: 'Registro criado com erros',
              description: `${failures.length} anexo(s) nao puderam ser enviados.`,
              variant: 'destructive',
            })
          } else {
            toast({
              title: 'Registro criado',
              description: 'O registro de evolucao foi salvo com sucesso.',
            })
          }
        } else {
          toast({
            title: 'Registro criado',
            description: 'O registro de evolucao foi salvo com sucesso.',
          })
        }
      }
      onSuccess?.()
    } catch (error) {
      toast({
        title: 'Erro',
        description: getErrorMessage(
          error,
          isEditMode
            ? 'Nao foi possivel atualizar o registro.'
            : 'Nao foi possivel criar o registro.',
        ),
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={form.handleSubmit(handleSubmit)}
      className="flex flex-col flex-1 min-h-0"
    >
      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {/* Record Date */}
        <Controller
          control={form.control}
          name="record_date"
          render={({ field, fieldState }) => (
            <div>
              <Label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                <CalendarDays className="h-4 w-4" />
                Data do atendimento
              </Label>
              <Input
                type="datetime-local"
                max={getCurrentDateTimeLocal()}
                value={field.value ?? ''}
                onChange={field.onChange}
                className="w-full sm:w-auto"
              />
              {fieldState.error && (
                <p className="text-sm text-red-500 mt-1">{fieldState.error.message}</p>
              )}
            </div>
          )}
        />

        {/* Tiptap Editor */}
        <div>
          {editor && <TiptapToolbar editor={editor} onTranscribed={handleTranscribed} />}
          <div className="border border-gray-200 rounded-b-lg bg-white overflow-hidden">
            <EditorContent editor={editor} />
          </div>
          {form.formState.errors.content && (
            <p className="text-sm text-red-500 mt-1">
              {form.formState.errors.content.message}
            </p>
          )}
        </div>

        {/* Attachments (only for new records) */}
        {!isEditMode && (
          <RecordAttachmentsUpload
            pendingFiles={pendingFiles}
            onAddFiles={handleAddFiles}
            onRemoveFile={handleRemoveFile}
          />
        )}

        {/* Tags */}
        <Controller
          control={form.control}
          name="tag_ids"
          render={({ field }) => (
            <TagSelector
              selectedIds={field.value ?? []}
              onChange={field.onChange}
            />
          )}
        />

        {/* Visibility toggle */}
        <Controller
          control={form.control}
          name="visibility"
          render={({ field }) => (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => field.onChange('shared')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors ${
                  field.value === 'shared'
                    ? 'border-teal-500 bg-teal-50 text-teal-700'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Users className="h-4 w-4" />
                Compartilhado com a equipe
              </button>
              <button
                type="button"
                onClick={() => field.onChange('family')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors ${
                  field.value === 'family'
                    ? 'border-teal-500 bg-teal-50 text-teal-700'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Heart className="h-4 w-4" />
                Compartilhar com familia
              </button>
              <button
                type="button"
                onClick={() => field.onChange('private')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors ${
                  field.value === 'private'
                    ? 'border-amber-500 bg-amber-50 text-amber-700'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Lock className="h-4 w-4" />
                Apenas para mim
              </button>
            </div>
          )}
        />
      </div>

      {/* Fixed footer with actions */}
      <div className="shrink-0 px-6 py-4 border-t bg-background flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting
            ? 'Salvando...'
            : isEditMode
              ? 'Salvar Alteracoes'
              : 'Salvar Registro'}
        </Button>
      </div>
    </form>
  )
}

// ============================================================
// Tiptap Toolbar
// ============================================================

interface TiptapToolbarProps {
  editor: ReturnType<typeof useEditor>
  onTranscribed: (text: string) => void
}

function TiptapToolbar({ editor, onTranscribed }: TiptapToolbarProps) {
  if (!editor) return null

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href
    const url = window.prompt('URL', previousUrl)

    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  return (
    <div className="flex items-center gap-1 p-2 border border-gray-200 rounded-t-lg border-b-0 bg-gray-50">
      <ToolbarButton
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
        title="Negrito"
      >
        <Bold className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        title="Italico"
      >
        <Italic className="h-4 w-4" />
      </ToolbarButton>
      <div className="w-px h-5 bg-gray-300 mx-1" />
      <ToolbarButton
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        title="Lista"
      >
        <List className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        title="Lista numerada"
      >
        <ListOrdered className="h-4 w-4" />
      </ToolbarButton>
      <div className="w-px h-5 bg-gray-300 mx-1" />
      <ToolbarButton
        active={editor.isActive('link')}
        onClick={setLink}
        title="Link"
      >
        <LinkIcon className="h-4 w-4" />
      </ToolbarButton>

      {/* Dictation button - aligned right */}
      <div className="ml-auto">
        <AudioRecorder onTranscribed={onTranscribed} />
      </div>
    </div>
  )
}

function ToolbarButton({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean
  onClick: () => void
  title: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded transition-colors ${
        active
          ? 'bg-teal-100 text-teal-700'
          : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  )
}
